import { useState, useEffect } from "react";
import { settingsApi } from "@/integrations/mysql/client";
import { useToast } from "@/hooks/use-toast";
import { Save, Loader2, Instagram } from "lucide-react";

interface NewsletterData {
  title: string;
  description: string;
  buttonText: string;
  buttonUrl: string;
}

const DEFAULT: NewsletterData = {
  title: "Acompanhe no Instagram",
  description: "Dicas diárias de nutrição, receitas saudáveis e conteúdo exclusivo direto no seu feed.",
  buttonText: "Seguir @brenda.nutricionista",
  buttonUrl: "https://www.instagram.com/brenda.nutricionista/",
};

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</label>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, maxLength }: { value: string; onChange: (v: string) => void; placeholder?: string; maxLength?: number }) {
  return (
    <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all" />
  );
}

function TextArea({ value, onChange, placeholder, rows = 3, maxLength }: { value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; maxLength?: number }) {
  return (
    <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows} maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all resize-none" />
  );
}

export function NewsletterManager() {
  const { toast } = useToast();
  const [data, setData]     = useState<NewsletterData>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    settingsApi.list()
      .then(list => {
        const raw = list.find(s => s.key === 'newsletter_instagram')?.value;
        if (raw) {
          try { setData({ ...DEFAULT, ...JSON.parse(raw) }); } catch { /**/ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const upd = <K extends keyof NewsletterData>(key: K, val: NewsletterData[K]) => {
    setData(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('newsletter_instagram', JSON.stringify(data));
      toast({ title: 'Salvo!' });
      setDirty(false);
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
    </div>
  );

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <div className="flex items-center gap-3 pb-3 border-b border-gray-100 mb-4">
        <div className="w-8 h-8 rounded-lg bg-brand-primary/8 flex items-center justify-center shrink-0">
          <Instagram className="w-4 h-4 text-brand-primary" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-800">Seção Instagram</h3>
          <p className="text-xs text-gray-400">Bloco verde exibido no final da página do Blog</p>
        </div>
      </div>

      <Field label="Título">
        <TextInput value={data.title} onChange={v => upd('title', v)} placeholder="Acompanhe no Instagram" maxLength={80} />
      </Field>

      <Field label={`Descrição (${data.description.length}/300)`}>
        <TextArea value={data.description} onChange={v => upd('description', v)} rows={3} maxLength={300}
          placeholder="Dicas diárias de nutrição, receitas saudáveis..." />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Texto do Botão">
          <TextInput value={data.buttonText} onChange={v => upd('buttonText', v)} placeholder="Seguir @brenda.nutricionista" maxLength={60} />
        </Field>
        <Field label="URL do Botão">
          <TextInput value={data.buttonUrl} onChange={v => upd('buttonUrl', v)} placeholder="https://www.instagram.com/..." maxLength={300} />
        </Field>
      </div>

      {dirty && (
        <div className="flex items-center justify-between bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-4 py-3">
          <p className="text-xs text-brand-primary font-medium">Alterações não salvas</p>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salvar
          </button>
        </div>
      )}
    </div>
  );
}
